import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminDecideQuestionApprovals, adminListQuestionApprovals } from '../api/admin';
import { parseJsonSafe } from '../api/client';
import MarkdownRenderer from '../components/MarkdownRenderer';

const PAGE_SIZE = 50;

function ageLabel(timestamp) {
  const value = new Date(timestamp).getTime();
  if (!Number.isFinite(value)) return 'Unknown age';
  const days = Math.max(0, Math.floor((Date.now() - value) / 86400000));
  if (days === 0) return 'Today';
  return `${days} day${days === 1 ? '' : 's'}`;
}

function DecisionDialog({ decision, count, note, onNoteChange, onCancel, onConfirm, busy }) {
  const approval = decision === 'approve';
  return (
    <div className="approval-dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="approval-dialog-title">
      <section className="approval-dialog">
        <h2 id="approval-dialog-title">Confirm {approval ? 'approval' : 'rejection'}</h2>
        <p>You are about to {decision} {count} question{count === 1 ? '' : 's'}.</p>
        {approval ? (
          <p><strong>Approval makes these questions live for students.</strong></p>
        ) : (
          <p>Rejected questions will not be served to students.</p>
        )}
        <label htmlFor="approval-note">Decision note (optional)</label>
        <textarea id="approval-note" value={note} maxLength={2000} onChange={(event) => onNoteChange(event.target.value)} />
        <div className="approval-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className={`btn ${approval ? 'btn-primary' : 'btn-danger'}`} onClick={onConfirm} disabled={busy}>
            {busy ? 'Submitting…' : `Confirm ${approval ? 'approval' : 'rejection'}`}
          </button>
        </div>
      </section>
    </div>
  );
}

function QuestionApprovalsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogDecision, setDialogDecision] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminListQuestionApprovals({ limit: PAGE_SIZE, offset });
      const body = await parseJsonSafe(response);
      if (!response.ok) throw new Error(body?.detail || 'Could not load question approvals.');
      setItems(Array.isArray(body?.items) ? body.items : []);
      setTotal(Number(body?.total || 0));
      setSelected(new Set());
      setExpanded(new Set());
    } catch (loadError) {
      setError(loadError.message || 'Could not load question approvals.');
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => { load(); }, [load]);

  const pageIds = useMemo(() => items.map((item) => item.id), [items]);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSelected = (id) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleExpanded = (id) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitDecision = async () => {
    const ids = Array.from(selected);
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        approve: dialogDecision === 'approve' ? ids : [],
        reject: dialogDecision === 'reject' ? ids : [],
        note: note.trim() || null,
      };
      const response = await adminDecideQuestionApprovals(payload);
      const body = await parseJsonSafe(response);
      if (!response.ok) throw new Error(body?.detail || 'Could not submit decisions.');
      setResults(Array.isArray(body?.results) ? body.results : []);
      setDialogDecision(null);
      setNote('');
      await load();
    } catch (decisionError) {
      setError(decisionError.message || 'Could not submit decisions.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="question-approvals-page">
      <header className="question-approvals-header">
        <div>
          <p className="eyebrow">Admin review gate</p>
          <h1>Question approvals</h1>
          <p>Review independently verified answer keys before making LLM-origin questions available to students.</p>
        </div>
        <div className="approval-pending-count" aria-label={`${total} questions pending`}>
          <strong>{total}</strong><span>pending</span>
        </div>
      </header>

      {error ? <p className="notice notice-error" role="alert">{error}</p> : null}
      {results.length > 0 ? (
        <section className="approval-results" aria-label="Decision results">
          <h2>Batch results</h2>
          <ul>{results.map((result) => <li key={`${result.id}-${result.result}`}>Question {result.id}: <strong>{result.result}</strong></li>)}</ul>
        </section>
      ) : null}

      {loading ? <p>Loading question approvals…</p> : null}
      {!loading && items.length === 0 ? <p className="approval-empty-state">No questions awaiting approval</p> : null}

      {!loading && items.length > 0 ? (
        <>
          <div className="approval-batch-bar">
            <label><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(pageIds))} /> Select all on page</label>
            <span>{selected.size} selected</span>
            <button type="button" className="btn btn-primary" disabled={!selected.size} onClick={() => setDialogDecision('approve')}>Approve</button>
            <button type="button" className="btn btn-danger" disabled={!selected.size} onClick={() => setDialogDecision('reject')}>Reject</button>
          </div>
          <div className="approval-table-wrap">
            <table className="approval-table">
              <thead><tr><th scope="col">Select</th><th scope="col">Family</th><th scope="col">Micro-skill</th><th scope="col">Verification</th><th scope="col">Age</th><th scope="col">Review</th></tr></thead>
              <tbody>
                {items.map((item) => {
                  const isExpanded = expanded.has(item.id);
                  const audit = item.answer_key_verification || {};
                  return [
                    <tr key={`row-${item.id}`}>
                      <td><input type="checkbox" aria-label={`Select question ${item.id}`} checked={selected.has(item.id)} onChange={() => toggleSelected(item.id)} /></td>
                      <td>{item.family_key}</td><td>{item.micro_skill_code || '—'}</td><td>{audit.verification_power || '—'}</td><td>{ageLabel(audit.verified_at || item.created_at)}</td>
                      <td><button type="button" className="btn btn-secondary" aria-expanded={isExpanded} onClick={() => toggleExpanded(item.id)}>{isExpanded ? 'Hide' : 'Review'}</button></td>
                    </tr>,
                    isExpanded ? (
                      <tr key={`detail-${item.id}`} className="approval-detail-row"><td colSpan="6">
                        <div className="approval-detail-grid">
                          <section><h3>Student view</h3><div className="markdown-block"><MarkdownRenderer content={item.stem_markdown} /></div>
                            {(item.parts || []).map((part) => <div key={part.part_label} className="approval-part"><strong>({part.part_label})</strong>{part.prompt_suffix_markdown ? <MarkdownRenderer content={part.prompt_suffix_markdown} /> : null}</div>)}
                          </section>
                          <section><h3>Stored answer key</h3>{(item.parts || []).map((part) => <div key={part.part_label} className="approval-answer"><strong>Part {part.part_label}</strong><pre>{JSON.stringify(part.expected_answer_json, null, 2)}</pre><p>Canonical answer: <MarkdownRenderer inline content={`$${part.canonical_answer_latex || ''}$`} /></p></div>)}
                            <h3>Verifier audit</h3><p>{audit.reason || 'No reason recorded'}</p><p><strong>Power:</strong> {audit.verification_power || '—'}</p><p><strong>Verified:</strong> {audit.verified_at || '—'}</p>
                          </section>
                        </div>
                      </td></tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
          <nav className="approval-pagination" aria-label="Question approval pages">
            <button type="button" className="btn btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
            <span>{offset + 1}–{Math.min(offset + items.length, total)} of {total}</span>
            <button type="button" className="btn btn-secondary" disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </nav>
        </>
      ) : null}

      {dialogDecision ? <DecisionDialog decision={dialogDecision} count={selected.size} note={note} onNoteChange={setNote} onCancel={() => setDialogDecision(null)} onConfirm={submitDecision} busy={submitting} /> : null}
    </main>
  );
}

export default QuestionApprovalsPage;
